import { useCallback, useEffect, useRef, useState } from "react";
import { fetchDashboard } from "../data/dashboardApi";
import { readCache, writeCache } from "../data/dashboardCache";
import { refreshMs, retryMs } from "../constants";

const empty = {
  teamTarget: {},
  bestSeller: {},
  deals: {},
  existantDeals: {},
  soldServices: [],
  yearRankList: [],
  rankList: [],
  amount: 0,
  scoredTowardsTarget: 0,
};

// Minimaal zoveel endpoints moeten lukken voordat we het scherm tonen.
// Daaronder is het beeld zo leeg dat opnieuw proberen beter is.
const minOk = 4;

// En deze twee moeten er sowieso bij zijn. Zonder rankList staat het
// teamtotaal op nul, zonder teamTarget is er geen target om tegen af te
// zetten; in beide gevallen klopt de hele bovenste helft van het scherm
// niet, hoeveel andere kaarten het ook doen.
const required = ["rankList", "teamTarget"];

// Haalt de dashboardcijfers op voor één maand en houdt ze bij.
// Mislukt het (deels), dan probeert hij het vanzelf elke 20 seconden opnieuw.
// Daarnaast kijkt hij elke minuut of er verkopen bij zijn gekomen; zonder dat
// zou het scherm pas na een herlaadbeurt iets nieuws laten zien.
export function useDashboardData(month, year) {
  const [data, setData] = useState(empty);
  const [isReady, setIsReady] = useState(false);

  const aliveRef = useRef(true);
  const abortRef = useRef(null);
  const retryRef = useRef(null);

  // skipCache: de minuutcontrole slaat de cache over, want anders
  // krijgt hij vijf minuten lang hetzelfde antwoord terug.
  const load = useCallback(async (skipCache = false) => {
    const cached = skipCache ? null : readCache(month, year);
    if (cached) {
      setData(cached);
      setIsReady(true);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const { data: fresh, okCount, ok } = await fetchDashboard(
        month,
        year,
        controller.signal,
      );
      if (!aliveRef.current) return;

      const missing = required.filter((key) => !ok[key]);
      if (okCount < minOk || missing.length) {
        const reason = missing.length
          ? `${missing.join(" en ")} ontbreekt`
          : `maar ${okCount} van de cijfers opgehaald`;
        console.warn(`[dashboard] ${reason}, over ${retryMs / 1000}s opnieuw`);
        retryRef.current = setTimeout(() => load(true), retryMs);
        return;
      }

      writeCache(month, year, fresh);
      setData(fresh);
      setIsReady(true);
    } catch (error) {
      if (error.name === "AbortError" || error.name === "CanceledError") return;
      if (!aliveRef.current) return;
      console.error("[dashboard] ophalen mislukt:", error);
      retryRef.current = setTimeout(() => load(true), retryMs);
    }
  }, [month, year]);

  useEffect(() => {
    aliveRef.current = true;
    setIsReady(false);
    load();

    const refresh = setInterval(() => load(true), refreshMs);

    return () => {
      aliveRef.current = false;
      abortRef.current?.abort();
      clearTimeout(retryRef.current);
      clearInterval(refresh);
    };
  }, [load]);

  // refresh: nú opnieuw ophalen, buiten de minuutronde om. Daar hangt het
  // aangaan van de tv aan: staat het scherm weer aan, dan wil je niet nog
  // een minuut wachten voor de kaart "Verkopen gemist" verschijnt. De cache
  // gaat eroverheen, anders krijg je vijf minuten lang hetzelfde antwoord.
  const refresh = useCallback(() => load(true), [load]);

  return { data, isReady, refresh };
}
